<template>
<!-- N.B. Loads some arbitrary string so that the span has some text so that we can compute the height-->
  <span v-if="!mounted" class="pre-mount-span">{{ currentValue || 'Loading...' }}</span>
  <textarea v-else
            v-model="currentValue"
            class="multiline-input"
            :style="{ 'height': height }"
            :placeholder="placeholder"
            :dir="dir"
            @input="onInput"
            @keydown.enter="onEnter"
            @keydown.escape="$emit('escape')"
            @focus="$emit('focus')"
            @blur="$emit('blur', $event)"
            @keyup="$emit('keyup', $event)" />
</template>

<script>
/** USAGE GUIDE FOR MULTILINE INPUT
 * Method 1: v-model
 * <multiline-input v-model="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will be updated with the new `value`
 * (therefore can do things like `this.value = ""` to clear the field)
 * (also be careful if `value` is remotely updated, it will overwrite the current changes)
 * - On input, `value` data field will changes accordingly
 *
 * Method 2: v-model and v-once
 * <multiline-input v-once v-model="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will NOT be updated with the new `value`
 * - On input, `value` will changes accordingly
 *
 * Method 3: value prop
 * <multiline-input :value="value" @input="onInput" @enter="onEnter" ref="input"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will be updated with the new `value`
 * - On input, `value` will NOT changes accordingly
 *  - You can use DOM refs to get the value (i.e. `this.$refs.input.$el.value`)
 *  - You can listen to input or enter event to get the value
 *
 * Method 4: value prop and v-once
 * <multiline-input v-once :value="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will NOT be updated with the new `value`
 * - On input, `value` will NOT changes accordingly (same as above, use DOM ref or events)
 */

// Emitted events: input [newValue], enter, escape, focus, keyup [$event]
import { observeIntersection } from 'bits/intersection'
export default {
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    newline: {
      type: Boolean,
      default: true,
    },
    dir: {
      type: String,
      default: 'auto',
    },
  },
  data () {
    return {
      currentValue: this.value,
      height: null,
      initialHeight: null,
      mounted: false,
    }
  },
  watch: {
    value (newValue) {
      this.currentValue = newValue || ''
      // Trigger a reset height so that if the text field is expanded
      // accross multiple lines, it will return to single line
      this.resetHeightToInitialHeight()
    },
  },
  mounted () {
    // Loads the span first and get the height of the text, then set
    // the height of the textarea to the acquired height
    this.initialHeight = this.$el.offsetHeight ? this.$el.offsetHeight + 'px' : this.getElementComputedLineHeight()
    if (this.initialHeight) {
      this.height = this.initialHeight
      this.mounted = true // loads the textarea for input
      return
    }
    this.$nextTick(() => {
      this.initialHeight = this.$el.scrollHeight ? this.$el.scrollHeight + 'px' : this.getElementComputedLineHeight()
      if (this.initialHeight) {
        this.height = this.initialHeight
        this.mounted = true
      } else {
        this.setInitialHeightOnAppearingWhenMounted()
      }
    })
  },
  methods: {
    focus () {
      this.$nextTick(() => {
        this.$el.focus()
      })
    },
    setInitialHeightOnAppearingWhenMounted () {
      const hadObserveIntersection = observeIntersection(this.$el, () => {
        this.initialHeight = this.$el.scrollHeight + 'px'
        this.height = this.initialHeight
        this.mounted = true // loads the textarea for input once height is set
      })
      if (!hadObserveIntersection) {
        this.mounted = true // still loads the textarea for input if observeIntersection fails
      }
    },
    getElementComputedLineHeight () {
      return window.getComputedStyle(this.$el).getPropertyValue('line-height')
    },
    resetHeightToInitialHeight () {
      this.height = this.initialHeight
    },
    expandFieldToFitText () {
      this.$nextTick(() => {
        this.height = this.$el.scrollHeight + 'px'
      })
    },
    validateInput () {
      let newValue = this.currentValue
      if (!this.newline) {
        const newValueWithoutNewlines = newValue.replace(/\n|\r/g, '')
        if (newValueWithoutNewlines !== newValue) {
          this.currentValue = newValueWithoutNewlines
          newValue = newValueWithoutNewlines
        }
      }
    },
    onInput (inputEvent) {
      // reset the height to minimal
      this.resetHeightToInitialHeight()
      if (inputEvent) {
        this.expandFieldToFitText()
      }

      // validate input
      this.validateInput()

      // trigger input event
      this.$emit('input', this.currentValue)
    },
    onEnter (e) {
      if (!this.newline) {
        this.$emit('enter', this.currentValue)
        e.preventDefault()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  textarea {
    resize: none;
    box-sizing: border-box;
  }
  .pre-mount-span {
    flex-grow: 1;
    padding-bottom: 8px;
  }
</style>
